import { Injectable } from '@angular/core';
import { BarChartVM, ChartTableData, ChartVM, PieChartVM } from '../../home/dashboard/chart-vm';
import {
    GroupByOptions,
    ReportFilterForm,
    ReportRequestForm,
    ReportResponse,
    ReportResponseForm,
    ReportRow,
    RowIndex,
} from 'src/app/core/models/report';
import { ActivityFiltersForm, Group } from 'src/app/core/models/chart';
import { ColorService } from './color.service';
import { DateUtilsService } from './utils/date-utils.service';
import { TranslateService } from '@ngx-translate/core';
import { UserSettings } from 'src/app/core/models/user-settings';

/**
 * @class ChartReportAdapterService
 *
 * @description The ChartReportAdapterService is meant to map the chart (partially legacy) related code to the new
 * API endpoint for reports. It builds the new request from the old parameters and maps the table report results back
 * to the existing bar chart, pie chart and table components.
 */
@Injectable({
    providedIn: 'root',
})
export class ChartReportAdapterService {
    autoColors: string[];
    userSettings?: UserSettings;

    constructor(
        colorService: ColorService,
        private readonly dateUtils: DateUtilsService,
        private readonly translate: TranslateService,
    ) {
        this.autoColors = colorService.getChartAutoColors();
    }

    // TODO This should be an injected dependency and not a setter.
    public setUserSettings(userSettings: UserSettings): void {
        this.userSettings = userSettings;
    }

    private getUserDateFormat(): string {
        if (this.userSettings && this.userSettings.dateFormat) {
            return this.userSettings.dateFormat;
        }
        return 'YYYY-MM-DD';
    }

    public getReportFilter(chartFilter: ActivityFiltersForm): ReportFilterForm {
        const tileIds = Object.values(chartFilter.tiles)
            .flat()
            .filter((e) => e !== undefined);
        const groupIds = chartFilter.groups ? chartFilter.groups.map((group: Group) => group.id) : [];
        const userIds: number[] = chartFilter.userIds ?? [];

        /* note filter logic:
           - when text in UI filled, then string "text"
           - when nothing filled, then empty string
           - when the flag "has note" is set, then true
           - when the flag "has no note" is set, then false
         */
        const hasNote = typeof chartFilter.note === 'boolean' ? chartFilter.note : undefined;
        const note = typeof chartFilter.note === 'string' && chartFilter.note !== '' ? chartFilter.note : undefined;

        const startDate = this.dateUtils.getLocalTimeIso(chartFilter.startDate, true);
        const endDate = this.dateUtils.getLocalTimeIso(chartFilter.endDate, true);

        return {
            note,
            hasNote,
            startDate,
            endDate,
            strictDate: chartFilter.strictDate,
            userIds: userIds.length > 0 ? userIds : undefined,
            tileIds: tileIds.length > 0 ? tileIds : undefined,
            billed: chartFilter.billed,
            groupIds: groupIds.length > 0 ? groupIds : undefined,
        };
    }

    private getDates(req: ReportRequestForm, barChartInterval: string): string[] {
        const start = req.reports[0].filter.startDate;
        const end = req.reports[0].filter.endDate;
        const dateFormat = this.getUserDateFormat();

        const dates = [];
        const dt = new Date(start);
        while (dt <= new Date(end)) {
            switch (barChartInterval) {
                case 'day':
                    dates.push(this.dateUtils.formatDateByInterval(dt, 'day', dateFormat)); // push a copy
                    dt.setDate(dt.getDate() + 1);
                    break;
                case 'week':
                    dates.push(this.dateUtils.formatDateByInterval(dt, 'week', dateFormat)); // push a copy
                    dt.setDate(dt.getDate() + 7);
                    break;
                case 'month':
                    dates.push(this.dateUtils.formatDateByInterval(dt, 'month', dateFormat)); // push a copy
                    dt.setMonth(dt.getMonth() + 1);
                    break;
                case 'year':
                    dates.push(this.dateUtils.formatDateByInterval(dt, 'year', dateFormat)); // push a copy
                    dt.setFullYear(dt.getFullYear() + 1);
                    break;
            }
        }
        return dates;
    }

    private static getBarChartInterval(req: ReportRequestForm): string {
        return req.reports[0].groupBy[1]; // TODO hardcoded array index, not so nice...
    }

    public getChartVM(req: ReportRequestForm, reportResponse: ReportResponseForm): ChartVM {
        const barChartInterval = ChartReportAdapterService.getBarChartInterval(req);
        const dates = this.getDates(req, barChartInterval);
        reportResponse = this.fillColors(reportResponse);

        return {
            dates,
            totalSum: reportResponse.reports[0].totalSeconds,
            highestName: 'fav',
            barChart: this.getBarChart(reportResponse.reports[0], dates, barChartInterval, req.reports[0].groupBy),
            pieChart: this.getPieChart(reportResponse.reports[1], req.reports[1].groupBy),
            tableData: this.getTableData(reportResponse.reports[1], req.reports[1].groupBy),
        };
    }

    /**
     * Sets an automatically chosen color for all colors with value 'none'
     * It will reuse the same colors for the same contexts for all report objects, e.g., "layer:1" (client) == 'Microsoft'
     * @param reportResponse The report response object, that will get adjusted for all colors: 'none' => auto color
     */
    private fillColors(reportResponse: ReportResponseForm): ReportResponseForm {
        const colorMap = new Map<string, string>();
        let nextColor = 0;

        const getColor = (groupValue: string): string => {
            const key = groupValue;
            let color: string;
            if (colorMap.has(key)) {
                color = colorMap.get(key)!;
            } else {
                color = this.autoColors[nextColor];
                colorMap.set(key, color);
                nextColor++;
                if (nextColor >= this.autoColors.length) {
                    nextColor = 0;
                }
            }
            return color;
        };

        reportResponse.reports.forEach((report) => {
            report.rows.forEach((row) => {
                const color = row[1];
                if (color && color === 'none') {
                    row[1] = getColor(row[2]);
                }
            });
        });
        return reportResponse;
    }

    private groupDataByGroupOneOnly(rows: ReportRow[]): ReportRow[] {
        const map = new Map<string, ReportRow>();

        rows.forEach((row) => {
            const group1 = row[RowIndex.GROUP1];
            const mapRow: ReportRow | undefined = map.get(group1);
            if (mapRow) {
                mapRow[RowIndex.SECONDS] = mapRow[RowIndex.SECONDS] + row[RowIndex.SECONDS];
                map.set(group1, mapRow);
            } else {
                map.set(group1, row);
            }
        });
        return Array.from(map.values());
    }

    private formatValue(value: string, groupBy: GroupByOptions, userDateFormat: string): string {
        if (groupBy === 'week') {
            const dateParts = value.split('-W');
            return this.dateUtils.formatWeekByValues(dateParts[0], dateParts[1]);
        }
        if (groupBy === 'day') {
            // Must not use new Date(value): the API's day value is a calendar date without any time
            // zone and new Date() would read it as UTC midnight, which is the previous day for every
            // user west of UTC (see #597).
            return this.dateUtils.formatDateByInterval(value, 'day', userDateFormat);
        }
        if (value === 'none' && groupBy === 'group') {
            return this.translate.instant('without_group');
        }
        if (value === '-' && groupBy.startsWith('layer')) {
            return this.translate.instant('without_layer');
        }
        if (value === '' && groupBy === 'note') {
            return this.translate.instant('without_note');
        }
        return value;
    }

    private getPieChart(response: ReportResponse, groupBy: GroupByOptions[]): PieChartVM {
        let data: ReportRow[] = structuredClone(response.rows); // create a copy of the report input data, as the code below changes it

        // manually group the data to only group1 if there are more groups
        if (response.headers.length > 3) {
            data = this.groupDataByGroupOneOnly(data);
        }

        const userDateFormat = this.getUserDateFormat();

        return {
            labels: data.map((row) => this.formatValue(row[RowIndex.GROUP1], groupBy[0], userDateFormat)),
            data: data.map((row) => Number(row[RowIndex.SECONDS]) / 3600),
            backgroundColor: data.map((row) => row[RowIndex.COLOR]),
        };
    }

    private getTableData(response: ReportResponse, groupBy: GroupByOptions[]): ChartTableData[] {
        const tableData: ChartTableData[] = [];
        const userDateFormat = this.getUserDateFormat();
        for (const row of response.rows) {
            tableData.push({
                group1: this.formatValue(row[RowIndex.GROUP1], groupBy[0], userDateFormat),
                // != null checks for not null and not undefined
                group2:
                    row[RowIndex.GROUP2] != null
                        ? this.formatValue(row[RowIndex.GROUP2], groupBy[1], userDateFormat)
                        : '',
                durationSeconds: Number(row[RowIndex.SECONDS]),
                backgroundColor: row[RowIndex.COLOR],
            });
        }
        return tableData;
    }

    private getBarChart(
        response: ReportResponse,
        dates: string[],
        interval: string,
        groupBy: GroupByOptions[],
    ): BarChartVM[] {
        const barChartData: BarChartVM[] = [];

        type TimeEntry = {
            seconds: number;
            date: string;
        };
        type TimeMap = Map<string, TimeEntry[]>;
        const chartData: TimeMap = new Map();

        type BarChartLabelMap = Map<string, BarChartVM>;
        const barChartLabelMap: BarChartLabelMap = new Map();

        const userDateFormat = this.getUserDateFormat();

        for (const row of response.rows) {
            const group1 = row[RowIndex.GROUP1];
            const seconds = Number(row[RowIndex.SECONDS]);

            if (!barChartLabelMap.has(group1)) {
                const color = row[RowIndex.COLOR].substring(0, 7);
                barChartLabelMap.set(group1, {
                    label: this.formatValue(group1, groupBy[0], userDateFormat),
                    backgroundColor: color,
                    borderWidth: 1,
                    borderColor: color,
                    borderSkipped: false,
                    borderRadiusOnAllStackedSeries: false,
                    data: [],
                });
            }
            if (!chartData.has(group1)) {
                chartData.set(group1, [
                    {
                        seconds,
                        date: this.dateUtils.formatDateByInterval(row[RowIndex.GROUP2], interval, userDateFormat),
                    },
                ]);
            } else {
                chartData.get(group1)?.push({
                    seconds,
                    date: this.dateUtils.formatDateByInterval(row[RowIndex.GROUP2], interval, userDateFormat),
                });
            }
        }

        for (const [label, bc] of barChartLabelMap) {
            const timeData = chartData.get(label);
            if (!timeData) continue;
            bc.data = [];
            dates.forEach((dt) => {
                const timeEntry = timeData.find((el) => el.date === dt);
                if (timeEntry) {
                    bc.data.push(timeEntry.seconds / 3600);
                } else {
                    bc.data.push(0);
                }
            });
            barChartData.push(bc);
        }
        return barChartData;
    }
}
